/**
 * As mesmas opções da vitrine (components/ProductMock/opcoes.js no
 * brindeme_template_1): o vendedor e o comprador falam com o mesmo motor de
 * composição, e divergir aqui faria a amostra do orçamento sair diferente da
 * do site.
 *
 * As demos de `/amostra-gravacoes` saíram do próprio motor, com um logotipo
 * genérico: uma peça rígida (garrafa) para as técnicas de superfície dura e
 * uma de tecido (camiseta) para as de tecido, para a comparação ser entre
 * gravações e não entre produtos. Refazê-las: bancada
 * `storage/app/amostra-samples/demos2.php` no backend.
 */
/**
 * O laser abre a lista de propósito: as cinco de tinta são quase iguais na
 * miniatura, então começar por elas dá a impressão de que a escolha não muda
 * nada. Com a mais diferente em primeiro, a diferença entre famílias aparece
 * antes de qualquer clique.
 */
const DEMOS = `${process.env.PUBLIC_URL ?? ''}/amostra-gravacoes`;

export const FAMILIAS = [
    { id: 'sem-tinta', titulo: 'Sem tinta — aparece o material' },
    { id: 'tinta', titulo: 'Tinta — impressa na superfície' },
    { id: 'relevo', titulo: 'Relevo e aplicação' },
];

export const TECNICAS = [
    {
        value: 'silk',
        label: 'Serigrafia (silk)',
        resumo: 'Tinta por tela, cor chapada.',
        onde: 'plano · 1 a 4 cores',
        familia: 'tinta',
        demo: DEMOS + '/silk.webp',
    },
    {
        value: 'tampografia',
        label: 'Tampografia',
        resumo: 'Carimbo de silicone: pega curva.',
        onde: 'curvas · peças pequenas',
        familia: 'tinta',
        demo: DEMOS + '/tampografia.webp',
    },
    {
        value: 'transfer',
        label: 'Transfer',
        resumo: 'Filme prensado a quente.',
        onde: 'tecido · aceita degradê',
        familia: 'tinta',
        demo: DEMOS + '/transfer.webp',
    },
    {
        value: 'uv',
        label: 'Impressão UV',
        resumo: 'Tinta curada por luz, leve relevo.',
        onde: 'superfície rígida',
        familia: 'tinta',
        demo: DEMOS + '/uv.webp',
    },
    {
        value: 'sublimacao',
        label: 'Sublimação',
        resumo: 'O corante entra na fibra.',
        onde: 'poliéster claro · não descasca',
        familia: 'tinta',
        demo: DEMOS + '/sublimacao.webp',
    },
    {
        value: 'laser',
        label: 'Laser',
        resumo: 'Tira a camada e revela o material.',
        onde: 'metal · madeira · uma cor',
        familia: 'sem-tinta',
        demo: DEMOS + '/laser.webp',
    },
    {
        value: 'bordado',
        label: 'Bordado',
        resumo: 'Fio costurado, em relevo.',
        onde: 'tecido · traço fino se perde',
        familia: 'relevo',
        demo: DEMOS + '/bordado.webp',
    },
    {
        value: 'adesivo',
        label: 'Adesivo',
        resumo: 'Impresso e colado por cima.',
        onde: 'qualquer superfície · sai fácil',
        familia: 'relevo',
        demo: DEMOS + '/adesivo.webp',
    },
];

export const tecnicaPor = valor => TECNICAS.find(item => item.value === valor) ?? null;

/** Gravação que o modal abre quando o material do produto é desconhecido. */
export const TECNICA_PADRAO = 'silk';

/**
 * A gravação mais usada em cada material — é ela que o modal marca ao abrir.
 * Mexer aqui é o suficiente para mudar o palpite; nada mais no código decide
 * isso.
 */
export const TECNICA_POR_MATERIAL = {
    metal: 'laser',
    madeira: 'laser',
    couro: 'laser',
    vidro: 'laser',
    ceramica: 'silk',
    plastico: 'tampografia',
    tecido: 'silk',
    papel: 'silk',
    borracha: 'silk',
};

export const tecnicaParaMaterial = material => TECNICA_POR_MATERIAL[material] ?? TECNICA_PADRAO;

/** Acabamentos sem tinta: a cor vem do material, não de uma escolha. */
export const SEM_TINTA = ['laser'];

/**
 * Técnicas sem tinta branca. Na sublimação o corante PENETRA o tecido e só
 * escurece — o branco da arte é o próprio tecido. No motor isso é o ramo
 * translúcido: branco multiplica por 1 e não muda pixel nenhum, então oferecer
 * a cor seria prometer uma gravação que sai invisível.
 */
export const SEM_BRANCO = ['sublimacao'];

const luminancia = cor => {
    const hex = String(cor ?? '').replace('#', '');

    if (hex.length !== 6) return 0;

    const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16));

    return 0.299 * r + 0.587 * g + 0.114 * b;
};

/** A técnica não entrega esta cor — vale para o branco e para quase-brancos. */
export const corImpossivel = (tecnica, cor) => Boolean(cor)
    && SEM_BRANCO.includes(tecnica)
    && luminancia(cor) > 235;

export const CORES_TINTA = [
    { value: null, label: 'Original' },
    { value: '#FFFFFF', label: 'Branco' },
    { value: '#1A1A1A', label: 'Preto' },
];

/**
 * "Outros" é o vazio: sem material informado o servidor deduz pela própria
 * foto. A lista existe porque esse palpite erra (plástico laranja parece
 * madeira) e a gravação depende dele.
 */
export const MATERIAIS = [
    { value: null, label: 'Outros' },
    { value: 'madeira', label: 'Madeira / bambu' },
    { value: 'metal', label: 'Metal' },
    { value: 'plastico', label: 'Plástico' },
    { value: 'vidro', label: 'Vidro' },
    { value: 'ceramica', label: 'Cerâmica' },
    { value: 'tecido', label: 'Tecido' },
    { value: 'couro', label: 'Couro' },
    { value: 'papel', label: 'Papel' },
    { value: 'borracha', label: 'Borracha' },
];

/**
 * Pistas de material na ficha do produto. Sem acento e em minúsculas: o texto
 * é normalizado antes de comparar, senão "alumínio" e "aluminio" viram dois
 * casos. A ordem da lista só desempata quando duas pistas caem na mesma
 * posição do texto.
 */
const PISTAS = [
    ['metal', [/\binox\b/, /\baluminio\b/, /\baco\b/, /\bmetal\b/, /\blatao\b/, /\bferro\b/]],
    ['tecido', [/\btecido\b/, /\balgodao\b/, /\bpoliester\b/, /\bnylon\b/, /\blona\b/, /\bbrim\b/, /\boxford\b/, /\bfeltro\b/, /\bmalha\b/, /\bsarja\b/, /\bjuta\b/, /\brpet\b/, /\bcanvas\b/]],
    ['couro', [/\bcouro\b/, /\bnapa\b/, /\bcamurca\b/]],
    ['madeira', [/\bmadeira\b/, /\bbambu\b/, /\bmdf\b/, /\bpinus\b/, /\bcortica\b/]],
    ['vidro', [/\bvidro\b/, /\bcristal\b/]],
    ['ceramica', [/\bceramica\b/, /\bporcelana\b/, /\blouca\b/]],
    ['papel', [/\bpapel\b/, /\bpapelao\b/, /\bkraft\b/, /\bcartao\b/, /\bcartolina\b/]],
    ['borracha', [/\bborracha\b/, /\bsilicone\b/, /\beva\b/, /\bneoprene\b/]],
    ['plastico', [/\bplastico\b/, /\bacrilico\b/, /\bpolipropileno\b/, /\bpoliestireno\b/, /\bresina\b/, /\bpp\b/, /\babs\b/, /\bpet\b/, /\bps\b/, /\bpvc\b/, /\btritan\b/]],
];

/**
 * Segunda camada: o TIPO da peça. A maioria dos nomes de catálogo não diz o
 * material — diz o que o produto é ("Caneca Térmica", "Ecobag", "Boné"), e o
 * material vem junto. Só entra quando nenhuma pista de material apareceu, e a
 * ordem importa: caneca térmica é inox, caneca sozinha é cerâmica.
 */
const TIPOS = [
    ['metal', [/\bcaneca termica\b/, /\bcopo termico\b/, /\bgarrafa termica\b/, /\bsqueeze\b/, /\bcantil\b/, /\btumbler\b/, /\bgarrafa\b/, /\bmarmita\b/, /\bchaveiro\b/]],
    ['ceramica', [/\bcaneca\b/, /\bcaneco\b/, /\bxicara\b/]],
    ['tecido', [/\bcamiseta\b/, /\bcamisa\b/, /\bpolo\b/, /\bbone\b/, /\bmochila\b/, /\bsacola\b/, /\becobag\b/, /\bbolsa\b/, /\bnecessaire\b/, /\bpochete\b/, /\btoalha\b/, /\bavental\b/, /\balmofada\b/, /\bchapeu\b/, /\bmanta\b/, /\bcobertor\b/]],
    ['papel', [/\bcaderno\b/, /\bbloco\b/, /\bagenda\b/, /\bcartao\b/, /\bcalendario\b/, /\bmarcador de pagina\b/]],
    ['vidro', [/\btaca\b/, /\bcopo long drink\b/]],
    ['plastico', [/\bcopo\b/, /\bcaneta\b/, /\bpower ?bank\b/, /\bcarregador\b/, /\bpen ?drive\b/, /\bmouse\b/, /\bcaixa de som\b/, /\bregua\b/, /\blancheira\b/, /\bporta ?copo\b/]],
];

const semAcento = texto => String(texto ?? '')
    .replace(/<[^>]*>/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/**
 * Primeiro procura o material dito com todas as letras ("alumínio", "algodão");
 * só depois cai no tipo da peça. Assim uma descrição que diz "em cerâmica"
 * vence o palpite pelo nome.
 *
 * Entre pistas do mesmo tipo ganha a que aparece antes no texto, e o nome vale
 * mais que a descrição: "Squeeze Alumínio com tampa de bambu" é de alumínio.
 *
 * Sem pista nenhuma devolve null, que é "Outros" na tela — e, no servidor, o
 * palpite pela própria foto.
 */
const procurar = (textos, tabela) => {
    for (const texto of textos) {
        let achado = null;

        for (const [material, padroes] of tabela) {
            for (const padrao of padroes) {
                const posicao = texto.search(padrao);

                if (posicao >= 0 && (achado === null || posicao < achado.posicao)) {
                    achado = { material, posicao };
                }
            }
        }

        if (achado) return achado.material;
    }

    return null;
};

export const materialPelaDescricao = (...textos) => {
    const limpos = textos.map(semAcento).filter((texto) => texto.trim());

    return procurar(limpos, PISTAS) ?? procurar(limpos, TIPOS);
};
